import { CaptionText } from '@/features/content/CaptionText';
import { MathText } from '@/features/content/MathText';
import {
  clampHeadingLevel,
  renderContentHtml,
  type HeadingLevel,
} from '@/features/content/contentHtml';
import { buildTableMarkup } from '@/features/content/tableUnitHtml';
import type {
  IframeUnitData,
  ImageUnitData,
  InlineVideoUnitData,
  NormalizedContentUnit,
  TableUnitData,
  TextUnitData,
} from '@/features/content/useComponent';

import { getYouTubeWatchUrl } from './videoUrlUtils';
import styles from './printContentUnits.module.css';


function PrintHeading({
  headingLevel,
  id,
  text,
}: {
  headingLevel?: HeadingLevel;
  id?: string;
  text?: string;
}) {
  if (!text) {
    return null;
  }

  const HeadingTag = `h${clampHeadingLevel(headingLevel)}` as const;

  return (
    <HeadingTag className={styles.heading} id={id}>
      <MathText text={text} />
    </HeadingTag>
  );
}

function PrintTextUnit({
  headingLevel,
  unit,
}: {
  headingLevel?: HeadingLevel;
  unit: TextUnitData;
}) {
  return (
    <article className={`${styles.unit} ${styles.textUnit}`}>
      <PrintHeading
        headingLevel={headingLevel}
        id={unit.anchorId}
        text={unit.numberedHeading ?? unit.heading}
      />
      <div className={`${styles.textContent} lesson-content`}>
        {renderContentHtml(unit.html)}
      </div>
      {unit.caption ? (
        <p className={styles.caption}>
          <CaptionText text={unit.caption} />
        </p>
      ) : null}
    </article>
  );
}

function PrintImageUnit({
  headingLevel,
  unit,
}: {
  headingLevel?: HeadingLevel;
  unit: ImageUnitData;
}) {
  const imageSizeClass =
    unit.size === 'small'
      ? styles.imageSmall
      : unit.size === 'large'
        ? styles.imageLarge
        : styles.imageMedium;

  return (
    <figure className={styles.figure}>
      <PrintHeading
        headingLevel={headingLevel}
        id={unit.anchorId}
        text={unit.numberedHeading ?? unit.heading}
      />
      <img
        alt={unit.alt}
        className={`${styles.image} ${imageSizeClass}`}
        src={unit.src}
      />
      {unit.caption ? (
        <figcaption className={styles.caption}>
          <CaptionText text={unit.caption} />
        </figcaption>
      ) : null}
    </figure>
  );
}

function PrintTableUnit({
  headingLevel,
  unit,
}: {
  headingLevel?: HeadingLevel;
  unit: TableUnitData;
}) {
  // Same `buildTableMarkup` + `renderContentHtml` as the screen, so the printed
  // table cannot drift from the one the student reads. The caption rides inside
  // the table as <caption>, which is why there is no separate caption element
  // here.
  const markup = buildTableMarkup(unit.html, {
    caption: unit.caption,
    headerRow: unit.headerRow,
  });

  return (
    <article className={`${styles.unit} ${styles.textUnit}`}>
      <PrintHeading
        headingLevel={headingLevel}
        id={unit.anchorId}
        text={unit.numberedHeading ?? unit.heading}
      />
      <div
        className={[
          styles.tableContent,
          styles[unit.style],
          styles[unit.density],
          'lesson-content',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {renderContentHtml(markup)}
      </div>
    </article>
  );
}

function PrintIframeUnit({
  headingLevel,
  unit,
}: {
  headingLevel?: HeadingLevel;
  unit: IframeUnitData;
}) {
  return (
    <article className={styles.resourceBlock}>
      <PrintHeading
        headingLevel={headingLevel}
        id={unit.anchorId}
        text={unit.numberedHeading ?? unit.heading ?? unit.title}
      />
      <span className={styles.resourceLabel}>Embedded activity</span>
      <a className={styles.resourceLink} href={unit.src}>
        {unit.title || unit.src}
      </a>
      <p className={styles.resourceMeta}>{unit.src}</p>
      {unit.caption ? (
        <p className={styles.resourceCaption}>
          <CaptionText text={unit.caption} />
        </p>
      ) : null}
    </article>
  );
}

function PrintInlineVideoUnit({
  headingLevel,
  unit,
}: {
  headingLevel?: HeadingLevel;
  unit: InlineVideoUnitData;
}) {
  const url = getYouTubeWatchUrl(unit.videoId);

  return (
    <article className={styles.resourceBlock}>
      <PrintHeading
        headingLevel={headingLevel}
        id={unit.anchorId}
        text={unit.numberedHeading ?? unit.heading ?? unit.title}
      />
      <span className={styles.resourceLabel}>Video URL</span>
      <a className={styles.resourceLink} href={url}>
        {url}
      </a>
      {unit.caption ? (
        <p className={styles.resourceCaption}>
          <CaptionText text={unit.caption} />
        </p>
      ) : null}
    </article>
  );
}

export function PrintContentUnit({
  headingLevel,
  includeVideoUrls,
  unit,
}: {
  headingLevel?: HeadingLevel;
  includeVideoUrls: boolean;
  unit: NormalizedContentUnit;
}) {
  if (unit.kind === 'text') {
    return <PrintTextUnit headingLevel={headingLevel} unit={unit} />;
  }

  if (unit.kind === 'image') {
    return <PrintImageUnit headingLevel={headingLevel} unit={unit} />;
  }

  if (unit.kind === 'iframe') {
    return <PrintIframeUnit headingLevel={headingLevel} unit={unit} />;
  }

  if (unit.kind === 'table') {
    return <PrintTableUnit headingLevel={headingLevel} unit={unit} />;
  }

  if (unit.kind === 'applet') {
    // Interactive applets have no static print representation.
    return null;
  }

  if (!includeVideoUrls) {
    return null;
  }

  return <PrintInlineVideoUnit headingLevel={headingLevel} unit={unit} />;
}

export function PrintContentUnits({
  headingLevel,
  includeVideoUrls,
  units,
}: {
  headingLevel?: HeadingLevel;
  includeVideoUrls: boolean;
  units: NormalizedContentUnit[];
}) {
  return units.map((unit) => (
    <PrintContentUnit
      headingLevel={headingLevel}
      includeVideoUrls={includeVideoUrls}
      key={unit.id}
      unit={unit}
    />
  ));
}
